import { useEffect, useRef, useState } from 'react'
import type { MallSceneManager } from './mallScene'

const JOYSTICK_RADIUS = 46   // px the knob can travel from the base's center
const JOYSTICK_DEADZONE = 8  // px of travel ignored, so a resting thumb doesn't drift

interface Props {
  sceneRef: React.RefObject<MallSceneManager | null>
}

/**
 * Fixed-position virtual joystick for mobile movement (bottom-left). Owns its
 * own touch listeners on its own DOM node rather than going through
 * useMallInputController: since it's rendered as a sibling that visually
 * overlaps the canvas, touches landing on it hit-test to this element and
 * never reach the canvas container underneath, so no coordination with the
 * look-drag handling is needed — the two simply never see the same touch.
 */
export default function MobileJoystick({ sceneRef }: Props) {
  const baseRef = useRef<HTMLDivElement>(null)
  const [knob, setKnob] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const touchId = useRef<number | null>(null)
  const center = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const base = baseRef.current
    if (!base) return

    const updateFromPoint = (clientX: number, clientY: number) => {
      let dx = clientX - center.current.x
      let dy = clientY - center.current.y
      const dist = Math.hypot(dx, dy)
      const clamped = Math.min(dist, JOYSTICK_RADIUS)
      if (dist > 0) {
        dx = (dx / dist) * clamped
        dy = (dy / dist) * clamped
      }
      setKnob({ x: dx, y: dy })

      const usable = Math.max(0, clamped - JOYSTICK_DEADZONE) / (JOYSTICK_RADIUS - JOYSTICK_DEADZONE)
      const nx = clamped > 0 ? dx / clamped : 0
      const ny = clamped > 0 ? dy / clamped : 0
      sceneRef.current?.setMoveInput(-ny * usable, nx * usable)
    }

    const onTouchStart = (e: TouchEvent) => {
      if (touchId.current !== null) return
      const t = e.changedTouches[0]
      touchId.current = t.identifier
      const rect = base.getBoundingClientRect()
      center.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      setDragging(true)
      updateFromPoint(t.clientX, t.clientY)
    }
    const onTouchMove = (e: TouchEvent) => {
      const t = Array.from(e.changedTouches).find((c) => c.identifier === touchId.current)
      if (!t) return
      e.preventDefault()
      updateFromPoint(t.clientX, t.clientY)
    }
    const onTouchEnd = (e: TouchEvent) => {
      const ended = Array.from(e.changedTouches).some((c) => c.identifier === touchId.current)
      if (!ended) return
      touchId.current = null
      setDragging(false)
      setKnob({ x: 0, y: 0 })
      sceneRef.current?.setMoveInput(0, 0)
    }

    base.addEventListener('touchstart', onTouchStart, { passive: true })
    base.addEventListener('touchmove', onTouchMove, { passive: false })
    base.addEventListener('touchend', onTouchEnd, { passive: true })
    base.addEventListener('touchcancel', onTouchEnd, { passive: true })

    return () => {
      base.removeEventListener('touchstart', onTouchStart)
      base.removeEventListener('touchmove', onTouchMove)
      base.removeEventListener('touchend', onTouchEnd)
      base.removeEventListener('touchcancel', onTouchEnd)
      sceneRef.current?.setMoveInput(0, 0)
    }
  }, [sceneRef])

  return (
    <div className={`mall3d-joystick-base${dragging ? ' mall3d-joystick-base--active' : ''}`} ref={baseRef}>
      <div
        className="mall3d-joystick-knob"
        style={{
          transform: `translate(${knob.x}px, ${knob.y}px)`,
          transition: dragging ? 'none' : 'transform 0.15s ease-out',
        }}
      />
    </div>
  )
}
